import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';

export interface EquityChartPoint {
  /** ISO 时间 */
  t: string;
  /** 净值(美元数值) */
  equity: number;
}

interface Props {
  points: EquityChartPoint[];
  height?: number;
  className?: string;
  /** 折线整体涨跌色调:up=绿 / down=红 / neutral=中性 */
  tone?: 'up' | 'down' | 'neutral';
}

const TONE_VAR: Record<NonNullable<Props['tone']>, string> = {
  up: 'rgb(var(--c-up))',
  down: 'rgb(var(--c-down))',
  neutral: 'rgb(var(--c-ink-2))',
};

/**
 * 净值曲线 — 自实现 SVG 平滑折线(Catmull-Rom),含高/低点标注、起点基线、
 * 末点亮点、按住/悬停十字光标读数。无网格无坐标轴,贴近 App 资产页风格(参考设计图)。
 *
 * 数据通过 props 注入(已是美元数值),不直连 API。空/单点时降级显示提示。
 */
export function EquityChart({ points, height = 220, tone = 'neutral', className }: Props) {
  const width = 720;
  const padL = 12;
  const padR = 12;
  const padT = 28; // 顶部留给高点标签
  const padB = 26; // 底部留给低点标签
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const model = useMemo(() => {
    if (points.length < 2) return null;
    const vals = points.map((p) => p.equity);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || Math.max(1, Math.abs(max) * 0.01);
    const pad = range * 0.12;
    const yMin = min - pad;
    const yMax = max + pad;
    const yRange = yMax - yMin;
    const n = points.length;

    const xToPx = (i: number) => padL + (i / (n - 1)) * innerW;
    const yToPx = (v: number) => padT + ((yMax - v) / yRange) * innerH;

    const px = points.map((p, i) => ({ x: xToPx(i), y: yToPx(p.equity), v: p.equity, t: p.t }));

    // Catmull-Rom → 三次贝塞尔,生成平滑曲线 path。
    let line = `M ${px[0]!.x.toFixed(1)} ${px[0]!.y.toFixed(1)}`;
    for (let i = 0; i < px.length - 1; i++) {
      const p0 = px[i - 1] ?? px[i]!;
      const p1 = px[i]!;
      const p2 = px[i + 1]!;
      const p3 = px[i + 2] ?? px[i + 1]!;
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      line += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    const area = `${line} L ${px[px.length - 1]!.x.toFixed(1)} ${(padT + innerH).toFixed(1)} L ${px[0]!.x.toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;

    const hiIdx = vals.indexOf(max);
    const loIdx = vals.indexOf(min);

    return { px, line, area, hiIdx, loIdx, min, max };
  }, [points, innerW, innerH, padT]);

  if (!model) {
    return (
      <div
        className={cn('flex items-center justify-center text-sm text-ink-3', className)}
        style={{ height }}
      >
        暂无净值数据
      </div>
    );
  }

  const color = TONE_VAR[tone];
  const { px, line, area, hiIdx, loIdx } = model;
  const last = px[px.length - 1]!;
  const hovered = hoverIdx != null ? px[hoverIdx] : null;
  const fmtUsd = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtTip = (iso: string) =>
    new Date(iso).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });

  // 高/低标签防溢出:夹在画布内
  const clampX = (x: number, w: number) => Math.max(2, Math.min(width - w - 2, x - w / 2));

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      className={cn('block select-none', className)}
      onMouseLeave={() => setHoverIdx(null)}
      onMouseMove={(e) => {
        // viewBox(720×height) 的宽高比通常与容器(width 100%)不一致,SVG 默认
        // preserveAspectRatio=xMidYMid meet 会等比缩放并居中留白;若按 getBoundingClientRect
        // 线性换算,两侧留白会让十字光标整体偏移、对不上鼠标。用 CTM 反变换精确还原 viewBox 坐标。
        const ctm = e.currentTarget.getScreenCTM();
        if (!ctm) return;
        const xRel = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse()).x;
        // 最近点
        let best = 0;
        let bestD = Infinity;
        for (let i = 0; i < px.length; i++) {
          const d = Math.abs(px[i]!.x - xRel);
          if (d < bestD) { bestD = d; best = i; }
        }
        setHoverIdx(best);
      }}
    >
      <defs>
        <linearGradient id="equityArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* 起点基线(虚线) */}
      <line
        x1={padL}
        x2={width - padR}
        y1={px[0]!.y}
        y2={px[0]!.y}
        stroke="rgb(var(--c-line))"
        strokeWidth={1}
        strokeDasharray="3 4"
        opacity={0.5}
      />

      {/* 面积渐隐 + 折线 */}
      <path d={area} fill="url(#equityArea)" stroke="none" />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

      {/* 高点标注 */}
      <g>
        <circle cx={px[hiIdx]!.x} cy={px[hiIdx]!.y} r={2.5} fill="rgb(var(--c-ink-1))" />
        <text
          x={clampX(px[hiIdx]!.x, 56) + 28}
          y={Math.max(12, px[hiIdx]!.y - 8)}
          fontSize="11"
          textAnchor="middle"
          fill="rgb(var(--c-ink-1))"
          className="num"
        >
          {fmtUsd(model.max)}
        </text>
      </g>
      {/* 低点标注 */}
      <g>
        <circle cx={px[loIdx]!.x} cy={px[loIdx]!.y} r={2.5} fill="rgb(var(--c-ink-2))" />
        <text
          x={clampX(px[loIdx]!.x, 56) + 28}
          y={Math.min(height - 6, px[loIdx]!.y + 16)}
          fontSize="11"
          textAnchor="middle"
          fill="rgb(var(--c-ink-2))"
          className="num"
        >
          {fmtUsd(model.min)}
        </text>
      </g>

      {/* 末点亮点 */}
      <circle cx={last.x} cy={last.y} r={3.5} fill={color} />
      <circle cx={last.x} cy={last.y} r={6} fill={color} opacity={0.18} />

      {/* 悬停十字光标 + 读数 */}
      {hovered && (
        <g>
          <line x1={hovered.x} x2={hovered.x} y1={padT - 8} y2={padT + innerH} stroke="rgb(var(--c-ink-3))" strokeWidth={1} strokeDasharray="2 3" />
          <circle cx={hovered.x} cy={hovered.y} r={3.5} fill="rgb(var(--c-brand))" />
          <g transform={`translate(${clampX(hovered.x, 120) + 60}, 12)`}>
            <rect x={-60} y={-10} width={120} height={20} rx={4} fill="rgb(var(--c-bg-4))" opacity={0.96} />
            <text x={0} y={4} fontSize="10.5" textAnchor="middle" fill="rgb(var(--c-ink-1))" className="num">
              {fmtTip(hovered.t)} · {fmtUsd(hovered.v)}
            </text>
          </g>
        </g>
      )}
    </svg>
  );
}
